import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import { FiMusic, FiImage, FiPlus, FiTrash2, FiX, FiBell } from 'react-icons/fi';
import { LuGamepad2 } from 'react-icons/lu';
import { NotesIcon } from '../icons/ServiceIcons';
import {
  Reminder,
  RepeatMode,
  REMINDER_COLORS,
  REMINDER_EMOJIS,
  REPEAT_LABELS,
  getReminders,
  addReminder,
  deleteReminder,
  toggleReminder,
  snoozeReminder,
  updateReminder,
  collectDueReminders,
  getNextReminder,
  formatCountdown,
} from '../../lib/reminders';
import './ServiceTopBar.css';

interface ServiceTopBarProps {
  /** "/admin" or "/dashboard" — used to build the Music / Photos links. */
  basePath: string;
}

// A short, pleasant alarm chime via the Web Audio API (no asset files needed).
let audioCtx: AudioContext | null = null;
function playChime() {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    const ctx = audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    // Two rising notes, repeated — gentle but noticeable.
    [0, 0.18, 0.55, 0.73].forEach((offset, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = i % 2 === 0 ? 880 : 1175; // A5 / D6
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.25, now + offset + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.35);
    });
  } catch {
    /* audio blocked — the visual ring still fires */
  }
}

const ServiceTopBar: React.FC<ServiceTopBarProps> = ({ basePath }) => {
  const [now, setNow] = useState(new Date());
  const [panelOpen, setPanelOpen] = useState(false);
  const [reminders, setReminders] = useState<Reminder[]>(() => getReminders());
  const [ringing, setRinging] = useState<Reminder[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const ringTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // New-reminder form state
  const [draftTime, setDraftTime] = useState('09:00');
  const [draftLabel, setDraftLabel] = useState('');
  const [draftRepeat, setDraftRepeat] = useState<RepeatMode>('once');
  const [draftEmoji, setDraftEmoji] = useState(REMINDER_EMOJIS[0]);
  const [draftColor, setDraftColor] = useState(REMINDER_COLORS[0]);

  const navigate = useNavigate();
  // Guard-then-land: block the default NavLink nav, run the unsaved-changes guard,
  // then navigate only if the user didn't cancel.
  const guardedGo = useCallback((to: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    void (async () => {
      if (!(await confirmDiscardChanges())) return;
      setFormDirty(false);
      navigate(to);
    })();
  }, [navigate]);

  const refresh = useCallback(() => setReminders(getReminders()), []);

  // Tick every second: update clock + check for due reminders.
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setNow(d);
      const due = collectDueReminders(d);
      if (due.length) {
        setRinging(prev => [...prev, ...due]);
        refresh();
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [refresh]);

  // Keep in sync when reminders change elsewhere (other tab / panel actions).
  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener('akt_reminders_changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('akt_reminders_changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [refresh]);

  // While something is ringing, repeat the chime every few seconds.
  useEffect(() => {
    if (ringing.length > 0) {
      playChime();
      ringTimer.current = setInterval(playChime, 3000);
    }
    return () => {
      if (ringTimer.current) {
        clearInterval(ringTimer.current);
        ringTimer.current = null;
      }
    };
  }, [ringing.length]);

  // Close the panel on outside click.
  useEffect(() => {
    if (!panelOpen) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [panelOpen]);

  // 24-hour clock, no AM/PM suffix.
  const clock = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  const next = getNextReminder(now);
  const enabledCount = reminders.filter(r => r.enabled).length;
  // Pulse the clock when the next alarm is within 60 seconds.
  const imminent = !!next && next.inMs <= 60_000;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    addReminder({ label: draftLabel, time: draftTime, repeat: draftRepeat, emoji: draftEmoji, color: draftColor });
    setDraftLabel('');
    setDraftRepeat('once');
    refresh();
  };

  // Creative quick-add: schedule a reminder N minutes from now.
  const quickAdd = (mins: number) => {
    const t = new Date(now.getTime() + mins * 60_000);
    const hhmm = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    addReminder({
      label: draftLabel || `In ${mins} min`,
      time: hhmm,
      repeat: 'once',
      emoji: draftEmoji,
      color: draftColor,
    });
    setDraftLabel('');
    refresh();
  };

  const dismissRing = (id: number) => setRinging(prev => prev.filter(r => r.id !== id));
  const snoozeRing = (id: number, mins: number) => {
    snoozeReminder(id, mins);
    updateReminder(id, { enabled: true });
    dismissRing(id);
    refresh();
  };

  return (
    <>
      <div className="service-topbar">
        {/* Centered cluster: Photos · Clock · Music — sized to match the left-nav icons */}
        <div className="topbar-group">
        {/* Photos service (icon, left of clock) — active state mirrors the left nav */}
        <NavLink to={`${basePath}/photos`} onClick={guardedGo(`${basePath}/photos`)} className={({ isActive }) => `svc-shortcut svc-photos ${isActive ? 'active' : ''}`} aria-label="Your Photos">
          <FiImage size={22} />
          <span className="svc-pop" aria-hidden="true">Photos</span>
        </NavLink>

        {/* Alarm clock widget */}
        <div className="topbar-clock-wrap" ref={panelRef}>
          <button
            className={`topbar-clock ${imminent ? 'imminent' : ''} ${panelOpen ? 'open' : ''}`}
            onClick={() => setPanelOpen(o => !o)}
            title={next ? `Next: ${next.reminder.emoji} ${next.reminder.label} (${formatCountdown(next.inMs)})` : 'Alarms & reminders'}
          >
            <span className="clock-time">{clock}</span>
            {enabledCount > 0 && (
              <span className="clock-count" title={`${enabledCount} reminder${enabledCount > 1 ? 's' : ''} set`}>
                <FiBell size={11} />{enabledCount}
              </span>
            )}
          </button>

          {panelOpen && (
            <div className="reminder-panel">
              <div className="reminder-panel-head">
                <strong>Alarms &amp; Reminders</strong>
                <button className="rp-close" onClick={() => setPanelOpen(false)} aria-label="Close">
                  <FiX size={16} />
                </button>
              </div>

              {/* Existing reminders */}
              <div className="reminder-list">
                {reminders.length === 0 && (
                  <div className="reminder-empty">No reminders yet — add one below.</div>
                )}
                {reminders.map(r => {
                  const [h, m] = r.time.split(':').map(Number);
                  const d = new Date();
                  d.setHours(h, m, 0, 0);
                  const t12 = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                  return (
                    <div key={r.id} className={`reminder-row ${r.enabled ? '' : 'disabled'}`}>
                      <span className="rr-emoji" style={{ background: r.color }}>{r.emoji}</span>
                      <div className="rr-body">
                        <span className="rr-time">{t12}</span>
                        <span className="rr-label">{r.label}</span>
                        <span className="rr-repeat">{REPEAT_LABELS[r.repeat]}</span>
                      </div>
                      <label className="rr-switch" title={r.enabled ? 'On' : 'Off'}>
                        <input
                          type="checkbox"
                          checked={r.enabled}
                          onChange={() => { toggleReminder(r.id); refresh(); }}
                        />
                        <span className="rr-slider" />
                      </label>
                      <button
                        className="rr-del"
                        onClick={() => { deleteReminder(r.id); refresh(); }}
                        aria-label="Delete reminder"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Add form */}
              <form className="reminder-add" onSubmit={handleAdd}>
                <div className="ra-row">
                  <input
                    type="time"
                    className="ra-time"
                    value={draftTime}
                    onChange={e => setDraftTime(e.target.value)}
                    required
                  />
                  <input
                    type="text"
                    className="ra-label"
                    placeholder="What's the reminder?"
                    value={draftLabel}
                    onChange={e => setDraftLabel(e.target.value)}
                    maxLength={60}
                  />
                </div>

                <div className="ra-row ra-pickers">
                  <div className="ra-emojis">
                    {REMINDER_EMOJIS.map(em => (
                      <button
                        type="button"
                        key={em}
                        className={`ra-emoji ${draftEmoji === em ? 'sel' : ''}`}
                        onClick={() => setDraftEmoji(em)}
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ra-row ra-pickers">
                  <div className="ra-colors">
                    {REMINDER_COLORS.map(c => (
                      <button
                        type="button"
                        key={c}
                        className={`ra-color ${draftColor === c ? 'sel' : ''}`}
                        style={{ background: c }}
                        onClick={() => setDraftColor(c)}
                        aria-label={`Color ${c}`}
                      />
                    ))}
                  </div>
                  <select
                    className="ra-repeat"
                    value={draftRepeat}
                    onChange={e => setDraftRepeat(e.target.value as RepeatMode)}
                  >
                    {(Object.keys(REPEAT_LABELS) as RepeatMode[]).map(k => (
                      <option key={k} value={k}>{REPEAT_LABELS[k]}</option>
                    ))}
                  </select>
                  <button type="submit" className="ra-add-btn"><FiPlus size={16} /> Add</button>
                </div>

                {/* Creative quick-add chips */}
                <div className="ra-quick">
                  <span className="ra-quick-label">Quick:</span>
                  {[5, 15, 30, 60].map(mins => (
                    <button type="button" key={mins} className="ra-quick-chip" onClick={() => quickAdd(mins)}>
                      +{mins < 60 ? `${mins}m` : '1h'}
                    </button>
                  ))}
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Music service (icon, right of clock) — active state mirrors the left nav */}
        <NavLink to={`${basePath}/music`} onClick={guardedGo(`${basePath}/music`)} className={({ isActive }) => `svc-shortcut svc-music ${isActive ? 'active' : ''}`} aria-label="Your Music">
          <FiMusic size={22} />
          <span className="svc-pop" aria-hidden="true">Music</span>
        </NavLink>

        {/* Games — take a break, play a classic. Active state mirrors the left nav */}
        <NavLink to={`${basePath}/games`} onClick={guardedGo(`${basePath}/games`)} className={({ isActive }) => `svc-shortcut svc-games ${isActive ? 'active' : ''}`} aria-label="Games">
          <LuGamepad2 size={22} />
          <span className="svc-pop" aria-hidden="true">Games</span>
        </NavLink>

        {/* Notes — capture ideas, lists, anything. Active state mirrors the left nav */}
        <NavLink to={`${basePath}/vault`} onClick={guardedGo(`${basePath}/vault`)} className={({ isActive }) => `svc-shortcut svc-notes ${isActive ? 'active' : ''}`} aria-label="Your Knowledge Vault" title="Your Knowledge Vault">
          <NotesIcon size={30} />
          <span className="svc-pop" aria-hidden="true">Knowledge Vault</span>
        </NavLink>
        </div>
      </div>

      {/* Ringing overlay — one card per firing reminder */}
      {ringing.length > 0 && (
        <div className="alarm-ring-overlay">
          {ringing.map(r => (
            <div key={r.id} className="alarm-ring-card" style={{ borderColor: r.color }}>
              <div className="arc-bell" style={{ color: r.color }}>
                <FiBell size={40} />
              </div>
              <div className="arc-emoji">{r.emoji}</div>
              <div className="arc-label">{r.label}</div>
              <div className="arc-time">
                {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </div>
              <div className="arc-actions">
                <button className="arc-snooze" onClick={() => snoozeRing(r.id, 5)}>Snooze 5 min</button>
                <button className="arc-dismiss" style={{ background: r.color }} onClick={() => dismissRing(r.id)}>
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ServiceTopBar;
